import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import config from '../../../../../../config';
import { VendorOrder } from '../../../../../../app/shared/models/vendor-order.model';

@Injectable({
  providedIn: 'root',
})
export class VendorOrdersService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${config.API_ENDPOINT}/api/vendor-orders`; // /no-acknowledge
  private cachedOrders: VendorOrder[] | null = null;

  private static readonly AWAITING_VENDOR_APPROVAL = 'AWAITING_VENDOR_APPROVAL';
  private static readonly AWAITING_VENDOR_SELECTION = 'AWAITING_VENDOR_SELECTION';

  getVendorOrders(
    showVideosOnly = false,
    forceRefresh = false,
  ): Observable<VendorOrder[]> {
    if (!showVideosOnly && this.cachedOrders && !forceRefresh) {
      return of(this.cachedOrders);
    }

    return this.http
      .get<VendorOrder[]>(`${this.apiUrl}?showVideosOnly=${showVideosOnly}`)
      .pipe(
        map((orders) => this.normalizeOrders(orders)),
        tap((orders) => {
          if (!showVideosOnly) {
            this.cachedOrders = orders ?? [];
          }
        }),
      );
  }

  getVendorOrderByUid(jobUid: string, forceRefresh = false): Observable<VendorOrder | undefined> {
    if (this.cachedOrders && !forceRefresh) {
      return of(this.cachedOrders.find((order) => order.jobUid === jobUid));
    }

    return this.getVendorOrders(false, forceRefresh).pipe(
      map((orders) => orders.find((order) => order.jobUid === jobUid)),
    );
  }

  private normalizeOrders(orders: VendorOrder[] | null | undefined): VendorOrder[] {
    if (!orders) {
      return [];
    }

    return orders.map((order) => {
      if (!this.shouldMapToAwaitingVendorSelection(order)) {
        return order;
      }

      return {
        ...order,
        jobStatus: VendorOrdersService.AWAITING_VENDOR_SELECTION,
      };
    });
  }

  private shouldMapToAwaitingVendorSelection(order: VendorOrder): boolean {
    if (order.jobStatus !== VendorOrdersService.AWAITING_VENDOR_APPROVAL) {
      return false;
    }

    return (order.estimates ?? []).some((estimate) => {
      const acceptedBy = estimate.quote?.acceptedBy?.trim();
      const acceptedDate = estimate.quote?.acceptedDate?.trim();

      return !!acceptedBy && !!acceptedDate;
    });
  }
}
